// Hourly bucketing for the TracPatch Steps and Temperature charts' "1 Day" view.
//
// Both charts reduce the raw records the page holds (every record for the
// patient — the server applies no date cap) to one entry per calendar day in
// the viewer's local time. The 1-Day view reduces the same records to one
// entry per HOUR of a single local day instead: temperature averages the
// readings that fall inside the hour, steps sums the records whose startTime
// falls inside it. Hours are local (moment default), matching the per-day
// views; the DATA VIEW grid keeps printing each timestamp in US Eastern like
// every other patient table.
import moment from 'moment'

export const HOURS_PER_DAY = 24

// "12a", "1a", … "12p", … "11p" — compact tick label for a 24-band axis.
export function compactHourLabel(hour) {
  const h = hour % 12 === 0 ? 12 : hour % 12
  return h + (hour < 12 ? 'a' : 'p')
}

// "10:00 AM – 11:00 AM" — the hour's span, for tooltips.
export function hourRangeLabel(hour) {
  const fmt = (h) => moment({ hour: h % 24, minute: 0 }).format('h:mm A')
  return fmt(hour) + ' – ' + fmt(hour + 1)
}

// Start and (exclusive) end of the local calendar day containing `day`.
export function dayBounds(day) {
  const start = moment(day).startOf('day')
  return { start, end: start.clone().add(1, 'day') }
}

// True when `day` is today or later (local) — where the forward arrow stops.
export function isTodayOrLater(day) {
  return moment(day).startOf('day').isSameOrAfter(moment().startOf('day'))
}

// Local day start of the earliest record, or null — where the back arrow stops.
export function earliestDay(records, timeKey) {
  let min = null
  ;(records || []).forEach((r) => {
    const t = r && r[timeKey] ? moment(r[timeKey]) : null
    if (t && t.isValid() && (!min || t.isBefore(min))) min = t
  })
  return min ? min.startOf('day') : null
}

function emptyHours(start, extra) {
  const hours = []
  for (let h = 0; h < HOURS_PER_DAY; h++) {
    hours.push({ hour: h, date: start.clone().hour(h).toDate(), time: null, ...extra() })
  }
  return hours
}

function inDay(t, start, end) {
  return t.isValid() && !t.isBefore(start) && t.isBefore(end)
}

// Temperature: 24 entries for `day`, one per hour. femurC / tibiaC are the
// mean (°C) of the readings inside the hour, null when the hour has none.
// `time` is the hour's latest reading timestamp (for the data grid).
// ⚠️ No longer used by the Temperature chart, which moved to 15-minute buckets
// (quarterHourTemperature) so a device coming off the leg mid-hour is visible.
// Kept: hourly averaging is still the right shape for anything that wants one.
export function hourlyTemperature(records, day) {
  const { start, end } = dayBounds(day)
  const hours = emptyHours(start, () => ({ femurSum: 0, femurCount: 0, tibiaSum: 0, tibiaCount: 0 }))
  ;(records || []).forEach((r) => {
    if (!r || !r.recorded_at) return
    const t = moment(r.recorded_at)
    if (!inDay(t, start, end)) return
    const slot = hours[t.hour()]
    // isPlausible rather than `!= null`, for the same reason the 15-minute
    // version uses it: no caller should have to rediscover that raw readings
    // can be garbage. This one averages worn and off-leg together, so it is
    // only a ceiling here, not a worn/off split.
    if (isPlausible(r.femur_temp_c)) {
      slot.femurSum += r.femur_temp_c
      slot.femurCount += 1
    }
    if (isPlausible(r.tibia_temp_c)) {
      slot.tibiaSum += r.tibia_temp_c
      slot.tibiaCount += 1
    }
    if (!slot.time || t.isAfter(moment(slot.time))) slot.time = r.recorded_at
  })
  return hours.map((s) => ({
    hour: s.hour,
    date: s.date,
    time: s.time,
    count: Math.max(s.femurCount, s.tibiaCount),
    femurC: s.femurCount ? s.femurSum / s.femurCount : null,
    tibiaC: s.tibiaCount ? s.tibiaSum / s.tibiaCount : null,
  }))
}

// Steps: 24 entries for `day`; `steps` totals the records whose startTime
// falls inside the hour (the per-day view buckets by startTime as well).
// 🔴 AN HOUR WITH NO RECORDS IS NOT AN HOUR WITH ZERO STEPS.
// Every hour of the day was returned with `steps: 0`, so a chart drawn at 11am
// plotted a flat zero line across the whole afternoon -- reading as "the
// patient did not move" for hours that have not happened yet. Worse, it is
// indistinguishable from a real zero, which is a genuine clinical observation:
// a patient who wore the device and did not walk.
//
// `count` is the number of records that fell in the hour. `steps` is null when
// that is zero, so callers can skip the point entirely; a real zero (records
// present, no steps in them) still comes back as 0 and is still plotted.
// ⚠️ No longer used by the Steps chart, which moved to 15-minute buckets
// (quarterHourSteps). Kept for anything that wants an hourly total.
export function hourlySteps(records, day) {
  const { start, end } = dayBounds(day)
  const hours = emptyHours(start, () => ({ steps: 0, count: 0 }))
  hours.forEach((h) => {
    h.label = compactHourLabel(h.hour)
  })
  ;(records || []).forEach((r) => {
    if (!r || !r.startTime) return
    const t = moment(r.startTime)
    if (!inDay(t, start, end)) return
    const slot = hours[t.hour()]
    slot.steps += Number(r.steps) || 0
    slot.count += 1
    if (!slot.time || t.isAfter(moment(slot.time))) slot.time = r.startTime
  })
  return hours.map((h) => ({ ...h, steps: h.count ? h.steps : null }))
}

// ── "Is the device on the leg?" ───────────────────────────────────────────
//
// 🔴 MEASURED, NOT GUESSED — and corrected once, which is the useful part.
//
// FIRST MEASUREMENT (2026-09-28, bench): off-leg 77.2-78.6 °F, on-leg
// 87.9-92.0 °F. Nine degrees of empty space, so 82 was placed mid-gap.
//
// 🔴 THAT WAS WRONG, and production showed why (patient day 2026-09-26, one
// reading per minute, `docs/database/diagnostics/temperature_worn_report.js`):
//
//   1. THE BENCH NEVER SAW THE CHARGER. On the charger the patch self-heats in
//      a ~10-minute cycle, each pulse +1 to +1.5 °F then decaying:
//        23:01 82.5 -> 81.6   23:12 83.1 -> 81.9   23:21 83.1 -> 81.7
//      Peaks reach 83.1 °F. Every one of those crossed an 82 °F floor, so a
//      patch sitting on its charger all night was reported as WORN, on and off,
//      roughly twice per 15-minute bucket.
//
//   2. AMBIENT IS NOT 77-78 °F. That was one cool room. The patient's readings
//      floor at 79.8-81.5 °F, which leaves 82 sitting inside the noise rather
//      than above it.
//
//   3. Removal is unmistakable and does NOT need a low floor to catch: the
//      patch fell 93 -> 82 °F in 28 minutes (20:13 87.0, 20:25 83.6, 20:41
//      81.9). Exponential cooling. A worn limb cannot do that.
//
// THE REAL GAP, from production rather than the bench:
//
//     charger-cycle peaks    max 83.1 °F
//     genuinely worn         86.7 °F and up
//
// 85 °F sits in the middle of that 3.6 °F gap with nothing in it. It is also
// the floor the VENDOR's own app uses (TracPatchEventDataManager.m drops
// anything outside 85-105 °F before upload) — they had evidently seen charger
// readings that a bench test does not produce.
//
// ⚠️ The old comment argued a floor near 85 would "start discarding real
// clinical readings" from a cooler post-operative limb. That reasoning still
// holds in principle, and is the reason to revisit this if a patient's worn
// readings ever land below 86 °F. It did not apply here: the failure was not a
// cold limb being missed, it was a charger being counted as a leg.
export const WORN_MIN_F = 85
export const WORN_MIN_C = (WORN_MIN_F - 32) / 1.8 // 29.44 °C — the vendor’s 29.4444

// Upper bound. This is NOT the other end of "worn" -- it is a plausibility
// limit, and the distinction decides where a failing reading goes.
//
// Below WORN_MIN_F the device is off the leg, which is a real observation: the
// charts grey those readings deliberately, so "not worn" stays distinguishable
// from "not syncing". Above TEMP_MAX_F there is no observation at all. Skin on
// a limb does not reach 110 °F -- core temperature at 107 °F is already
// life-threatening -- so such a reading is sensor garbage, and routing it to
// the off-leg bucket would plot it in grey and stretch the Y axis exactly as if
// we had kept it. It is discarded from BOTH buckets instead.
//
// 110 rather than the 105 the vendor's own app uses (TracPatchEventDataManager.m
// drops anything outside 85-105 °F before upload). We cannot re-derive a reading
// we discard, so the bound is set where it can only catch nonsense, never an
// inflamed or infected knee running hot.
export const TEMP_MAX_F = 110
export const TEMP_MAX_C = (TEMP_MAX_F - 32) / 1.8 // 43.33 °C

// Could this have come from a temperature sensor on a body at all? Readings
// that fail this are dropped entirely, never counted as worn OR off-leg.
export const isPlausible = (celsius) => celsius != null && celsius <= TEMP_MAX_C

export const isWorn = (celsius) => isPlausible(celsius) && celsius >= WORN_MIN_C

// Share of readings taken while the device was actually on the leg. Null when
// there are no readings at all -- 0% would claim it was worn and shouldn't be,
// which is a different thing from never having reported.
export function wornPercent(buckets) {
  let worn = 0
  let off = 0
  ;(buckets || []).forEach((b) => {
    worn += b.wornCount || 0
    off += b.offCount || 0
  })
  const total = worn + off
  return total ? Math.round((worn / total) * 100) : null
}

// ── 15-minute buckets, for the Temperature 1-Day view ─────────────────────
export const SLOTS_PER_DAY = 96
export const QUARTER_MINUTES = 15

// "10:15 – 10:30" — the slot's span, for tooltips.
export function quarterRangeLabel(slot) {
  const fmt = (m) => moment({ hour: Math.floor(m / 60) % 24, minute: m % 60 }).format('h:mm A')
  return fmt(slot * QUARTER_MINUTES) + ' – ' + fmt((slot + 1) * QUARTER_MINUTES)
}

// Temperature at 15-minute resolution for one local day.
//
// An hourly average hides exactly what a clinician needs to see here: the
// device coming off the leg mid-hour. Measured 2026-09-28, off-leg readings sat
// at 77-78 °F and on-leg at 88-92 °F, and the changeover took under fifteen
// minutes -- averaged across an hour that transition becomes one lukewarm
// number that is true of neither state.
export function quarterHourTemperature(records, day) {
  const { start, end } = dayBounds(day)
  const slots = []
  for (let i = 0; i < SLOTS_PER_DAY; i++) {
    slots.push({
      slot: i,
      hour: Math.floor((i * QUARTER_MINUTES) / 60),
      date: start.clone().add(i * QUARTER_MINUTES, 'minutes').toDate(),
      time: null,
      femurSum: 0, femurCount: 0, tibiaSum: 0, tibiaCount: 0,
      offFemurSum: 0, offFemurCount: 0, offTibiaSum: 0, offTibiaCount: 0,
    })
  }
  ;(records || []).forEach((r) => {
    if (!r || !r.recorded_at) return
    const t = moment(r.recorded_at)
    if (!inDay(t, start, end)) return
    const idx = Math.floor((t.diff(start, 'minutes')) / QUARTER_MINUTES)
    const slot = slots[idx]
    if (!slot) return
    // Worn and off-leg readings are averaged SEPARATELY. Mixing them produces a
    // number that describes neither -- see the 80.9 °F slice on 2026-09-28,
    // which was the device being strapped on mid-slot.
    // Implausible readings fall through both branches -- see TEMP_MAX_F.
    if (isPlausible(r.femur_temp_c)) {
      if (isWorn(r.femur_temp_c)) { slot.femurSum += r.femur_temp_c; slot.femurCount += 1 }
      else { slot.offFemurSum += r.femur_temp_c; slot.offFemurCount += 1 }
    }
    if (isPlausible(r.tibia_temp_c)) {
      if (isWorn(r.tibia_temp_c)) { slot.tibiaSum += r.tibia_temp_c; slot.tibiaCount += 1 }
      else { slot.offTibiaSum += r.tibia_temp_c; slot.offTibiaCount += 1 }
    }
    if (!slot.time || t.isAfter(moment(slot.time))) slot.time = r.recorded_at
  })
  return slots.map((s) => ({
    slot: s.slot,
    hour: s.hour,
    date: s.date,
    time: s.time,
    count: Math.max(s.femurCount + s.offFemurCount, s.tibiaCount + s.offTibiaCount),
    wornCount: Math.max(s.femurCount, s.tibiaCount),
    offCount: Math.max(s.offFemurCount, s.offTibiaCount),
    // On-leg averages — what the chart plots.
    femurC: s.femurCount ? s.femurSum / s.femurCount : null,
    tibiaC: s.tibiaCount ? s.tibiaSum / s.tibiaCount : null,
    // Off-leg averages — kept, not discarded, so the chart can grey them. A
    // device off the leg for six hours is itself a finding; silently dropping
    // those readings makes "not worn" indistinguishable from "not syncing".
    offFemurC: s.offFemurCount ? s.offFemurSum / s.offFemurCount : null,
    offTibiaC: s.offTibiaCount ? s.offTibiaSum / s.offTibiaCount : null,
  }))
}

// Cadence: 24 entries for `day`. Same Option A definition the per-day view
// uses -- walking minutes only, so an hour with no walking is a GAP (null),
// never a zero. A zero would drag the line to the floor and make a quiet hour
// look like a collapse in gait quality.
// ⚠️ No longer used by the Cadence chart, which moved to the 15-minute MAX
// (quarterHourCadence) on 2026-10-14. Kept for anything that wants the hour.
export function hourlyCadence(records, day) {
  const { start, end } = dayBounds(day)
  const hours = emptyHours(start, () => ({ totalSteps: 0, walkingMinutes: 0 }))
  hours.forEach((h) => {
    h.label = compactHourLabel(h.hour)
  })
  ;(records || []).forEach((r) => {
    if (!r || !r.startTime) return
    const t = moment(r.startTime)
    if (!inDay(t, start, end)) return
    if (!r.steps || r.steps <= 0) return // walking minutes only
    const slot = hours[t.hour()]
    slot.totalSteps += Number(r.steps) || 0
    slot.walkingMinutes += 1
    if (!slot.time || t.isAfter(moment(slot.time))) slot.time = r.startTime
  })
  return hours.map((s) => ({
    ...s,
    avgCadence: s.walkingMinutes ? s.totalSteps / s.walkingMinutes : null,
  }))
}

// Cadence at 15-minute resolution for one local day: the PEAK walking minute
// in each slot, not the mean. Each steps record is one walking minute, so a
// record's steps are its cadence; the slot's maxCadence is the best of them.
// A slot with no walking minute is a gap (null), never a zero, as above.
// walkingMinutes / totalSteps are kept for the tooltip; `time` is the slot's
// latest record (for the data grid).
export function quarterHourCadence(records, day) {
  const { start, end } = dayBounds(day)
  const slots = []
  for (let i = 0; i < SLOTS_PER_DAY; i++) {
    slots.push({
      slot: i,
      hour: Math.floor((i * QUARTER_MINUTES) / 60),
      date: start.clone().add(i * QUARTER_MINUTES, 'minutes').toDate(),
      time: null,
      maxCadence: null,
      walkingMinutes: 0,
      totalSteps: 0,
    })
  }
  ;(records || []).forEach((r) => {
    if (!r || !r.startTime) return
    const t = moment(r.startTime)
    if (!inDay(t, start, end)) return
    const steps = Number(r.steps) || 0
    if (steps <= 0) return // walking minutes only
    const slot = slots[Math.floor(t.diff(start, 'minutes') / QUARTER_MINUTES)]
    if (!slot) return
    slot.maxCadence = slot.maxCadence == null ? steps : Math.max(slot.maxCadence, steps)
    slot.walkingMinutes += 1
    slot.totalSteps += steps
    if (!slot.time || t.isAfter(moment(slot.time))) slot.time = r.startTime
  })
  return slots
}

// Steps at 15-minute resolution for one local day: 96 slots, each the total
// of the records that started in it. Same null rule as hourlySteps -- a slot
// with no record is null (the device was silent) and the line breaks there; a
// slot whose records add up to 0 is a real 0 (worn, not walking). `time` is
// the slot's latest record timestamp.
export function quarterHourSteps(records, day) {
  const { start, end } = dayBounds(day)
  const slots = []
  for (let i = 0; i < SLOTS_PER_DAY; i++) {
    slots.push({
      slot: i,
      hour: Math.floor((i * QUARTER_MINUTES) / 60),
      date: start.clone().add(i * QUARTER_MINUTES, 'minutes').toDate(),
      time: null,
      steps: 0,
      count: 0,
    })
  }
  ;(records || []).forEach((r) => {
    if (!r || !r.startTime) return
    const t = moment(r.startTime)
    if (!inDay(t, start, end)) return
    const slot = slots[Math.floor(t.diff(start, 'minutes') / QUARTER_MINUTES)]
    if (!slot) return
    slot.steps += Number(r.steps) || 0
    slot.count += 1
    if (!slot.time || t.isAfter(moment(slot.time))) slot.time = r.startTime
  })
  return slots.map((s) => ({ ...s, steps: s.count ? s.steps : null }))
}
